import React, { useState, useEffect, useCallback } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import "./Hero.css";

// Slide 0 is always the hero intro. Slides 1+ are the feature cards.
const features = [
  {
    title: "Enhance Customer Satisfaction",
    description:
      "Give every caller a faster, more personal experience with context-aware conversations that resolve issues on the first try.",
  },
  {
    title: "Eliminate Call Abandonment",
    description:
      "Smart routing and shorter wait times keep customers on the line, so fewer calls drop before they reach an agent.",
  },
  {
    title: "Reduce Team Burnout",
    description:
      "Automate repetitive interactions and balance workloads across your team, so agents can focus on calls that need a human touch.",
  },
  {
    title: "Contextual Human Handover",
    description:
      "When a call needs a person, agents get full conversation history instantly, no repeating themselves, no lost context.",
  },
  {
    title: "Real-Time Data Sync",
    description:
      "Every call, note, and outcome syncs straight to your CRM the moment it happens, keeping your team aligned and up to date.",
  },
];

const SLIDE_COUNT = features.length + 1; // +1 for the hero intro slide
const AUTOPLAY_MS = 5000;

export default function Hero() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goToNext = useCallback(() => {
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev + 1) % SLIDE_COUNT);
    setTimeout(() => setIsTransitioning(false), 400);
  }, []);

  const goToPrevious = useCallback(() => {
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev === 0 ? SLIDE_COUNT - 1 : prev - 1));
    setTimeout(() => setIsTransitioning(false), 400);
  }, []);

  const goToSlide = useCallback(
    (index) => {
      if (index === currentIndex) return;
      setIsTransitioning(true);
      setCurrentIndex(index);
      setTimeout(() => setIsTransitioning(false), 400);
    },
    [currentIndex]
  );

  useEffect(() => {
    const interval = setInterval(goToNext, AUTOPLAY_MS);
    return () => clearInterval(interval);
  }, [goToNext]);

  return (
    <section className="hero-carousel">
      <div className="hero-carousel-overlay">
        <div className="container hero-carousel-container">
          <div className="hc-wrapper">
            <div
              className="hc-track"
              style={{
                transform: `translateX(-${currentIndex * 100}%)`,
                transition: isTransitioning
                  ? "transform 0.4s ease-in-out"
                  : "none",
              }}
            >
              {/* Slide 0: Hero intro content */}
              <div className="hc-slide hc-slide--hero">
                <div className="hero-content">
                  <h1>
                    Convert Leads With Our
                    <br />
                    <span>AI Powered</span> Call Center
                  </h1>

                  <p>
                    Turn every customer interaction into a sales opportunity
                    with our integrated call center solution. Manage inbound
                    and outbound calls, track conversations, and connect
                    seamlessly with your CRM to deliver faster responses,
                    build stronger customer relationships, and convert more
                    leads into loyal customers.
                  </p>

                  <div className="hero-buttons">
                    <a href="#services" className="hero-btn">
                      REQUEST A DEMO
                    </a>
                  </div>
                </div>
              </div>

              {/* Slides 1+: Feature cards */}
              {features.map((feature, index) => (
                <div className="hc-slide hc-slide--card" key={index}>
                  <div className="feature-card">
                    <h1>{feature.title}</h1>
                    <p>{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* <button
              className="hc-arrow hc-arrow-left"
              onClick={goToPrevious}
              aria-label="Previous slide"
            >
              <FaChevronLeft />
            </button>

            <button
              className="hc-arrow hc-arrow-right"
              onClick={goToNext}
              aria-label="Next slide"
            >
              <FaChevronRight />
            </button> */}
          </div>

          <div className="hc-dots">
            {Array.from({ length: SLIDE_COUNT }).map((_, index) => (
              <button
                key={index}
                className={`hc-dot ${index === currentIndex ? "active" : ""}`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}